import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { eden } from "@/lib/eden";
import { edenData } from "@/lib/eden-data";
import type { OrderStatus } from "@/types/billing";
import type { ConversationPatch, StreamEvent } from "@/types/chat";

type MessageNew = Extract<StreamEvent, { type: "message.new" }>;
type MessageMediaEvent = Extract<StreamEvent, { type: "message.media" }>;

export type ConnectionStatus = "connecting" | "open" | "closed";

export interface EventStreamHandlers {
  onMessageNew?: (event: MessageNew) => void;
  onMessageMedia?: (event: MessageMediaEvent) => void;
  onConversationChanged?: (conversationId: string, patch?: ConversationPatch) => void;
  onConversationRead?: (conversationId: string) => void;
  onChannelChanged?: (channelId: string) => void;
  onChannelQuota?: (event: Extract<StreamEvent, { type: "channel.quota" }>) => void;
  onOrderChanged?: (orderId: string, status: OrderStatus) => void;
  onReconnect?: () => void;
}

interface EventStreamContextValue {
  subscribe: (handlers: EventStreamHandlers) => () => void;
  status: ConnectionStatus;
}

// EXPORTED so tests can construct a fake provider
export const EventStreamContext = createContext<EventStreamContextValue | null>(null);

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000];
const HIDDEN_CLOSE_MS = 5 * 60_000;
const JITTER_LOW = 0.75;
const JITTER_HIGH = 1.25;

function jitter(ms: number): number {
  return Math.round(ms * (JITTER_LOW + Math.random() * (JITTER_HIGH - JITTER_LOW)));
}

/**
 * Build the absolute SSE URL. EventSource cannot carry headers, so the ticket
 * is the credential and rides in the query string. We prefix with
 * `VITE_API_BASE_URL` so the stream targets the API origin (not the SPA
 * origin), matching the rest of the generated client.
 */
function buildStreamUrl(ticket: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || "";
  return `${base}/v1/stream?ticket=${encodeURIComponent(ticket)}`;
}

function parseJson<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Provides a single SSE connection for the entire authenticated session.
 * Handles the full lifecycle:
 *
 *  - POST `/v1/stream/ticket` → fresh 60s ticket
 *  - `new EventSource('/v1/stream?ticket=...')`
 *  - On `hello`: declare connection open, fire `onReconnect` so subscribers
 *    refetch REST state (covers the disconnect window).
 *  - On `message.new` / `conversation.changed` / `conversation.read` /
 *    `channel.changed` / `order.changed`: route to matching subscriber callbacks.
 *  - On error or close: cycle back through a fresh ticket with bounded
 *    exponential backoff (1s, 2s, 4s, 8s, ±25% jitter).
 *  - On `visibilitychange → hidden` for >5 min: close to free server slots;
 *    reopen on `visible` (which also force-fires `onReconnect` so subscribers
 *    rehydrate anything that drifted while idle).
 *
 * Mount at ProtectedShell so the connection is shared across all protected
 * pages — a single EventSource per authenticated session.
 */
export function EventStreamProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const handlersRef = useRef<Set<EventStreamHandlers>>(new Set());

  const subscribe = useCallback((h: EventStreamHandlers) => {
    handlersRef.current.add(h);
    return () => {
      handlersRef.current.delete(h);
    };
  }, []);

  useEffect(() => {
    // Refs mirroring the useChatStream approach for guard flags and handles
    const esRef = { current: null as EventSource | null };
    const retryTimerRef = {
      current: null as ReturnType<typeof setTimeout> | null,
    };
    const ticketAbortRef = { current: null as AbortController | null };
    const hiddenTimerRef = {
      current: null as ReturnType<typeof setTimeout> | null,
    };
    const attemptRef = { current: 0 };
    const closedRef = { current: false };
    // Guard against concurrent openStream() invocations. handleVisibility can
    // fire repeatedly while a ticket fetch is in flight; without this, multiple
    // EventSources would race and the earlier ones would leak.
    const openingRef = { current: false };

    function clearRetry(): void {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    }

    function teardown(): void {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      if (ticketAbortRef.current) {
        ticketAbortRef.current.abort();
        ticketAbortRef.current = null;
      }
      clearRetry();
    }

    function fireAll<K extends keyof EventStreamHandlers>(
      key: K,
      ...args: Parameters<NonNullable<EventStreamHandlers[K]>>
    ): void {
      for (const h of handlersRef.current) {
        const fn = h[key] as ((...a: typeof args) => void) | undefined;
        fn?.(...args);
      }
    }

    function scheduleRetry(): void {
      if (closedRef.current) return;
      setStatus("connecting");
      const idx = Math.min(attemptRef.current, BACKOFF_MS.length - 1);
      const delay = jitter(BACKOFF_MS[idx]!);
      attemptRef.current += 1;
      clearRetry();
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        void openStream();
      }, delay);
    }

    async function openStream(): Promise<void> {
      if (closedRef.current) return;
      if (openingRef.current) return;
      openingRef.current = true;
      setStatus("connecting");
      ticketAbortRef.current = new AbortController();

      let ticket: string;
      try {
        const data = edenData(
          await eden.v1.stream.ticket.post({}, { fetch: { signal: ticketAbortRef.current.signal } }),
        );
        ticket = data.ticket;
      } catch {
        ticketAbortRef.current = null;
        openingRef.current = false;
        scheduleRetry();
        return;
      }
      ticketAbortRef.current = null;
      if (closedRef.current) {
        openingRef.current = false;
        return;
      }

      const es = new EventSource(buildStreamUrl(ticket));
      esRef.current = es;
      openingRef.current = false;

      es.addEventListener("hello", () => {
        attemptRef.current = 0;
        setStatus("open");
        fireAll("onReconnect");
      });

      es.addEventListener("message.new", (e) => {
        const parsed = parseJson<MessageNew>((e as MessageEvent).data);
        if (parsed) fireAll("onMessageNew", parsed);
      });

      es.addEventListener("message.media", (e) => {
        const parsed = parseJson<MessageMediaEvent>((e as MessageEvent).data);
        if (parsed?.conversationId && parsed.platformMsgId && parsed.media?.url) fireAll("onMessageMedia", parsed);
      });

      es.addEventListener("conversation.changed", (e) => {
        const parsed = parseJson<{
          conversationId: string;
          patch?: ConversationPatch;
        }>((e as MessageEvent).data);
        if (parsed?.conversationId) fireAll("onConversationChanged", parsed.conversationId, parsed.patch);
      });

      es.addEventListener("conversation.read", (e) => {
        const parsed = parseJson<{ conversationId: string }>((e as MessageEvent).data);
        if (parsed?.conversationId) fireAll("onConversationRead", parsed.conversationId);
      });

      es.addEventListener("channel.changed", (e) => {
        const parsed = parseJson<{ channelId: string }>((e as MessageEvent).data);
        if (parsed?.channelId) fireAll("onChannelChanged", parsed.channelId);
      });

      es.addEventListener("channel.quota", (e) => {
        const parsed = parseJson<Extract<StreamEvent, { type: "channel.quota" }>>((e as MessageEvent).data);
        if (parsed?.channelId) fireAll("onChannelQuota", parsed);
      });

      es.addEventListener("order.changed", (e) => {
        const parsed = parseJson<{ orderId: string; status: OrderStatus }>((e as MessageEvent).data);
        if (parsed?.orderId && parsed.status) fireAll("onOrderChanged", parsed.orderId, parsed.status);
      });

      es.onerror = () => {
        es.close();
        if (esRef.current === es) esRef.current = null;
        scheduleRetry();
      };
    }

    function handleVisibility(): void {
      if (document.visibilityState === "hidden") {
        if (hiddenTimerRef.current) clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = setTimeout(() => {
          teardown();
          setStatus("closed");
        }, HIDDEN_CLOSE_MS);
      } else {
        if (hiddenTimerRef.current) {
          clearTimeout(hiddenTimerRef.current);
          hiddenTimerRef.current = null;
        }
        if (!esRef.current && !retryTimerRef.current && !openingRef.current) {
          void openStream();
        }
      }
    }

    void openStream();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      closedRef.current = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      if (hiddenTimerRef.current) {
        clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = null;
      }
      teardown();
      setStatus("closed");
    };
  }, []);

  return <EventStreamContext.Provider value={{ subscribe, status }}>{children}</EventStreamContext.Provider>;
}

export function useEventStream(): EventStreamContextValue {
  const ctx = useContext(EventStreamContext);
  if (!ctx) {
    throw new Error("useEventStream must be used within <EventStreamProvider>");
  }
  return ctx;
}
